import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { notifyWorker, getOwnerIds } from '../../lib/notifyWorker'

function fmtDate(s) {
  if (!s) return ''
  const d = new Date(s + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' })
}

// Shows any pending replacement_offers where this user is `offered_to`.
// Accept / decline updates the relevant rows.
export default function ReplacementOfferInbox({ onChange }) {
  const { user, profile } = useAuth()
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(async () => {
    if (!user) return
    setLoading(true)
    const { data } = await supabase.from('replacement_offers')
      .select('*, day_off_requests:day_off_request_id(*, profiles:user_id(full_name))')
      .eq('offered_to', user.id)
      .eq('status', 'pending')
      .order('created_at', { ascending: true })
    setOffers(data || [])
    setLoading(false)
  }, [user])

  useEffect(() => { load() }, [load])

  const accept = async (offer) => {
    const req = offer.day_off_requests
    if (!req) return
    setBusyId(offer.id)

    // Mark this offer accepted
    await supabase.from('replacement_offers').update({
      status: 'accepted', responded_at: new Date().toISOString(),
    }).eq('id', offer.id)

    // Cancel siblings
    await supabase.from('replacement_offers').update({
      status: 'declined', responded_at: new Date().toISOString(),
    }).eq('day_off_request_id', req.id).neq('id', offer.id).eq('status', 'pending')

    // Mark request
    await supabase.from('day_off_requests').update({
      replacement_id: user.id, replacement_status: 'accepted',
    }).eq('id', req.id)

    // Swap schedule rows:
    //   - requester → day_off
    //   - me → scheduled, inherit job_id
    const date = req.request_date
    const { data: reqRow } = await supabase.from('worker_schedule')
      .select('id, job_id').eq('user_id', req.user_id).eq('schedule_date', date).maybeSingle()
    const jobId = reqRow?.job_id || null
    if (reqRow?.id) {
      await supabase.from('worker_schedule').update({ status: 'day_off', job_id: null, updated_at: new Date().toISOString() }).eq('id', reqRow.id)
    } else {
      await supabase.from('worker_schedule').insert({ user_id: req.user_id, schedule_date: date, status: 'day_off' })
    }
    const { data: myRow } = await supabase.from('worker_schedule')
      .select('id').eq('user_id', user.id).eq('schedule_date', date).maybeSingle()
    if (myRow?.id) {
      await supabase.from('worker_schedule').update({ status: 'scheduled', job_id: jobId, updated_at: new Date().toISOString() }).eq('id', myRow.id)
    } else {
      await supabase.from('worker_schedule').insert({ user_id: user.id, schedule_date: date, status: 'scheduled', job_id: jobId })
    }

    // Notify requester + owner
    const ownerIds = await getOwnerIds()
    await notifyWorker({
      userId: req.user_id,
      type: 'replacement_accepted',
      title: (profile?.full_name || 'Someone') + ' accepted',
      message: 'Covering ' + fmtDate(date),
      link: '/schedule?request=' + req.id,
      relatedId: req.id,
    })
    await Promise.all(ownerIds.map((id) => notifyWorker({
      userId: id,
      type: 'replacement_accepted',
      title: 'Replacement accepted',
      message: (profile?.full_name || 'Worker') + ' is covering ' + (req.profiles?.full_name || 'teammate') + ' on ' + fmtDate(date),
      link: '/schedule?request=' + req.id,
      relatedId: req.id,
    })))

    setBusyId(null)
    await load()
    if (onChange) onChange()
  }

  const decline = async (offer) => {
    const req = offer.day_off_requests
    setBusyId(offer.id)

    await supabase.from('replacement_offers').update({
      status: 'declined', responded_at: new Date().toISOString(),
    }).eq('id', offer.id)

    // Check if all offers declined → notify requester + owner
    const { data: remaining } = await supabase.from('replacement_offers')
      .select('status').eq('day_off_request_id', req.id)
    const allGone = (remaining || []).every((o) => o.status === 'declined')
    if (allGone && remaining.length > 0) {
      await supabase.from('day_off_requests').update({ replacement_status: 'declined' }).eq('id', req.id)
      const ownerIds = await getOwnerIds()
      await notifyWorker({
        userId: req.user_id,
        type: 'replacement_declined',
        title: 'No one could cover',
        message: fmtDate(req.request_date),
        link: '/schedule?request=' + req.id,
        relatedId: req.id,
      })
      await Promise.all(ownerIds.map((id) => notifyWorker({
        userId: id,
        type: 'replacement_not_found',
        title: 'No replacement found',
        message: (req.profiles?.full_name || 'Worker') + ' — ' + fmtDate(req.request_date) + ' (short-staffed)',
        link: '/schedule?request=' + req.id,
        relatedId: req.id,
      })))
    } else {
      // Notify requester only
      await notifyWorker({
        userId: req.user_id,
        type: 'replacement_declined',
        title: (profile?.full_name || 'Someone') + ' declined',
        message: fmtDate(req.request_date),
        link: '/schedule?request=' + req.id,
        relatedId: req.id,
      })
    }

    setBusyId(null)
    await load()
    if (onChange) onChange()
  }

  if (loading) return null
  if (offers.length === 0) return null

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>SHIFT COVERAGE REQUESTS</div>
      <div style={styles.list}>
        {offers.map((o) => {
          const req = o.day_off_requests
          const who = req?.profiles?.full_name || 'A teammate'
          const date = req?.request_date
          return (
            <div key={o.id} style={styles.row}>
              <div style={styles.main}>
                <div style={styles.title}>{who} wants you to cover</div>
                <div style={styles.date}>{fmtDate(date)}</div>
                {req?.reason && <div style={styles.reason}>Reason: {req.reason}</div>}
              </div>
              <div style={styles.actions}>
                <button disabled={busyId === o.id} onClick={() => accept(o)} style={styles.acceptBtn}>ACCEPT</button>
                <button disabled={busyId === o.id} onClick={() => decline(o)} style={styles.declineBtn}>DECLINE</button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const styles = {
  wrap: { marginBottom: '20px', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '4px', backgroundColor: 'rgba(180,140,60,0.05)', fontFamily: "'Barlow', sans-serif" },
  header: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  list: { display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', flexWrap: 'wrap' },
  main: { flex: 1, minWidth: '200px' },
  title: { fontSize: '14px', color: '#ffffff', fontWeight: 500 },
  date: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '1.5px', color: '#b48c3c', marginTop: '2px' },
  reason: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px', fontStyle: 'italic' },
  actions: { display: 'flex', gap: '6px' },
  acceptBtn: { backgroundColor: '#4a8c6a', border: 'none', borderRadius: '3px', padding: '8px 14px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  declineBtn: { backgroundColor: 'transparent', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', padding: '8px 14px', color: '#e05c5c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', cursor: 'pointer' },
}
